import type { SVGProps } from "react";
import type { C1_EloItem_Details, C1_EloItem_Type } from "../types/C1_EloTypes";
import { C1_EloItem_PlugType } from "../types/C1_EloTypes";
import { C1_ELO_PLUG_TYPE_LABELS } from "../C1_uiLabels";

/** Installation-plan symbols. Function labels qualify equipment without a unique plan sign.
 * Socket reference: EN 60617 11-13-04; connection box: EN 60617 11-12-05.
 * https://gallery.proficad.com/Schaltzeichen/Hausinstallationen/Steckdosen/Schutzkontaktsteckdose.html
 */
export function C1_DinSymbol({ symbolType, details, ...props }: SVGProps<SVGSVGElement> & {
  symbolType?: C1_EloItem_Type;
  details?: Partial<C1_EloItem_Details>;
}) {
  const label = (text: string) => <text x={12} y={12} textAnchor="middle" dominantBaseline="central"
    stroke="none" fill="currentColor" fontFamily="Arial, sans-serif" fontSize={text.length > 3 ? 4 : 6}>{text}</text>;
  let symbol;
  switch (symbolType) {
    case "light":
      symbol = <><circle cx={12} cy={12} r={8} /><path d="M6.3 6.3 17.7 17.7 M6.3 17.7 17.7 6.3" /></>;
      break;
    case "plug": {
      const plug = details?.plugType ?? C1_EloItem_PlugType.V230;
      const power = plug === C1_EloItem_PlugType.V230 || plug.startsWith(C1_EloItem_PlugType.V230Switch);
      symbol = <>
        <path d="M4 18 A8 8 0 0 1 20 18 M12 10 V3" />
        {power ? <path d="M7 6 H17" /> : <text x={12} y={17} textAnchor="middle" stroke="none" fill="currentColor" fontSize={4}>{C1_ELO_PLUG_TYPE_LABELS[plug]}</text>}
        {plug.startsWith(C1_EloItem_PlugType.V230Switch) && <path d="M17 9 21 5 M20 4 22 6" />}
      </>;
      break;
    }
    case "blind":
    case "jalousie":
      symbol = <><circle cx={12} cy={12} r={8} />{label("M")}</>;
      break;
    case "heating":
      symbol = <><path d="M2 12 H5 M19 12 H22" /><rect x={5} y={8} width={14} height={8} /><path d="M8 8 V16 M12 8 V16 M16 8 V16" /></>;
      break;
    case "window_contact":
      symbol = <><path d="M3 19 H7 M17 19 H21 M7 19 16 8" /><circle cx={7} cy={19} r={1} /><circle cx={17} cy={19} r={1} /></>;
      break;
    case "button":
      symbol = <><circle cx={10} cy={15} r={5} /><path d="M13.5 11.5 20 5 M17 3 22 8" /></>;
      break;
    case "fixed_power":
      symbol = <><rect x={5} y={5} width={14} height={14} /><path d="M5 5 19 19 M5 19 19 5" /></>;
      break;
    default: {
      // Supplemental function identifiers are intentionally not presented as unique DIN signs.
      const names: Partial<Record<C1_EloItem_Type, string>> = {
        camera: "CAM", door_station: "SP", door_lock: "TÖ", thermostat: "ϑ", panel: "HMI", intermediate_distribution: "UV",
        motion_sensor: details?.sensorTypes?.map(sensor => ({motion: "PIR", temperature: "ϑ", humidity: "RH", co2: "CO₂", smoke: "RM", water_leak_detector: "WM", window_contact: "MK"})[sensor]).join("/") || "PIR",
      };
      symbol = <><rect x={3} y={5} width={18} height={14} />{label(symbolType ? names[symbolType] ?? "?" : "?")}</>;
    }
  }
  return <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" {...props}>{symbol}</svg>;
}

/** Neutral group-number marker for the presentation menu. */
export function C1_TechnicalViewIcon(props: SVGProps<SVGSVGElement>) {
  return <svg width={24} height={24} viewBox="0 0 24 24" {...props}>
    <circle cx={12} cy={12} r={10} fill="#e5e7eb" stroke="currentColor" strokeWidth={1} />
    <text x={12} y={12} dominantBaseline="central" textAnchor="middle" fontFamily="Arial, sans-serif"
      fontSize={11} fontWeight={600} fill="currentColor">12</text>
  </svg>;
}

export function C1_DinViewIcon(props: SVGProps<SVGSVGElement>) {
  return <C1_DinSymbol symbolType={"plug" as C1_EloItem_Type} {...props} />;
}
